import React, { useEffect, useState } from "react"
import { IoIosArrowRoundBack } from "react-icons/io"
import { useHistory } from "react-router-dom"
import { BASE_URL, fetcher } from "../../utils/api"
import Spinner from "../../components/Spinner"
import { BsPerson, BsCalendar, BsClock, BsTelephone, BsEnvelope, BsGeoAlt } from "react-icons/bs"
import useSWR from "swr"
import { ErrorNotification, SuccessNotification } from "../../utils"

const AppointmentDetailView = (props) => {
  const history = useHistory()
  const appointment = props?.location?.state?.state
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({})
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const { data, isLoading, mutate } = useSWR(
    `appointments/${appointment?.id}`,
    fetcher
  )

  useEffect(() => {
    if (data) {
      setEditData({
        status: data.status
      })
    }
  }, [data])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A'
    return timeString.substring(0, 5)
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'confirmed':
        return 'bg-green-100 text-green-700'
      case 'cancelled':
        return 'bg-red-100 text-red-700'
      case 'completed':
        return 'bg-blue-100 text-blue-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  // Function to handle API error responses
  const handleApiError = (response, errorMessage = 'Operation failed') => {
    if (response.status === 400) {
      return response.json().then(errorData => {
        // Handle field-specific errors
        if (errorData && typeof errorData === 'object') {
          const fieldErrors = {}
          Object.keys(errorData).forEach(field => {
            if (Array.isArray(errorData[field])) {
              fieldErrors[field] = errorData[field].join(', ')
            } else if (typeof errorData[field] === 'string') {
              fieldErrors[field] = errorData[field]
            } else if (typeof errorData[field] === 'object') {
              fieldErrors[field] = Object.values(errorData[field]).join(', ')
            }
          })
          setErrors(fieldErrors)

          // Show the first error as a notification
          const firstError = Object.values(fieldErrors)[0]
          if (firstError) {
            ErrorNotification(firstError)
          }
          return fieldErrors
        }
        throw new Error(errorMessage)
      })
    } else {
      throw new Error(errorMessage)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    setErrors({}) // Clear previous errors

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${BASE_URL}appointments/${data.id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      })

      if (response.ok) {
        SuccessNotification('Appointment updated successfully!')
        setIsEditing(false)
        setErrors({})
        mutate()
      } else {
        await handleApiError(response, 'Failed to update appointment')
      }
    } catch (error) {
      ErrorNotification(error.message || 'Failed to update appointment')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkComplete = async () => {
    setLoading(true)
    setErrors({}) // Clear previous errors

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${BASE_URL}appointments/${data.id}/complete/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        SuccessNotification('Appointment marked as completed!')
        setErrors({})
        mutate()
      } else {
        await handleApiError(response, 'Failed to mark appointment as completed')
      }
    } catch (error) {
      ErrorNotification(error.message || 'Failed to mark appointment as completed')
    } finally {
      setLoading(false)
    }
  }

  const getInitials = (name) => {
    if (!name) return 'N/A'
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  return (
    <>
      <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10 space-y-6">
        <div className="w-full flex flex-col space-y-2 md:flex-row md:items-center md:justify-between">
          <div className="w-full">
            <button onClick={() => history.goBack()} className="flex flex-row items-center">
              <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
              <p className="text-sm font-light capitalize dark:text-white">
                Back
              </p>
            </button>

            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold dark:text-white">
                Appointment Details
              </h2>

              {data && (
                <div className="flex gap-3">
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
                    >
                      Edit Appointment
                    </button>
                  ) : (
                    <>
                      <button
                          onClick={() => {
                            setIsEditing(false)
                            setErrors({}) // Clear errors when canceling
                          }}
                        className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
                      >
                        {loading ? 'Saving...' : 'Save Appointment'}
                      </button>
                    </>
                  )}
                  {data.status === 'confirmed' && !isEditing && (
                    <button
                      onClick={handleMarkComplete}
                      disabled={loading}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Marking...' : 'Mark Complete'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="w-full h-96 flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {!isLoading && data && (
          <div className="w-full h-full space-y-6">
            {/* Appointment Details Card */}
            <div className="bg-white p-2 rounded-lg shadow-sm dark:bg-dark">
              <div className="border p-4 rounded-lg border-gray-200 dark:border-border-dark">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Appointment ID:</span>
                      <span className="font-semibold text-base dark:text-white">#{data?.id || 'N/A'}</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Date:</span>
                      <span className="font-semibold text-base dark:text-white">{formatDate(data?.date)}</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Time:</span>
                      <span className="font-semibold text-base dark:text-white">{formatTime(data?.time)}</span>
                    </div>
                  </div>

                  {/* Middle Column */}
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Status:</span>
                      {isEditing ? (
                        <div>
                          <select
                            value={editData.status}
                            onChange={(e) => {
                              setEditData({ ...editData, status: e.target.value })
                              // Clear status error when user changes the value
                              if (errors.status) {
                                setErrors(prev => ({ ...prev, status: null }))
                              }
                            }}
                            className={`py-3 capitalize px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white ${errors.status ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                              }`}
                          >
                            {/* <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>*/}
                            {data?.can_be_cancelled && <option value="cancelled">Cancelled</option>}
                            <option value="completed">Completed</option>
                          </select>
                          {errors.status && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                              {errors.status}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className={`inline-flex px-3 py-1 w-28 rounded-full text-xs font-medium capitalize ${getStatusColor(data?.status)}`}>
                          {data?.status}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Upcoming:</span>
                      <span className="font-semibold text-base dark:text-white">
                        {data?.is_upcoming ? 'Yes' : 'No'}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Can be Cancelled:</span>
                      <span className="font-semibold text-base dark:text-white">
                        {data?.can_be_cancelled ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Created:</span>
                      <span className="font-semibold text-base dark:text-white">{formatDate(data?.created_at)}</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 text-sm mb-1">Last Updated:</span>
                      <span className="font-semibold text-base dark:text-white">{formatDate(data?.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Farmer Details */}
            <div className="bg-white p-2 rounded-lg shadow-sm dark:bg-dark">
              <div className="border p-4 rounded-lg border-gray-200 dark:border-border-dark">
                <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
                  Farmer Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 h-16 w-16">
                      {data.farmer?.profile_image ? (
                        <img
                          src={data.farmer.profile_image}
                          alt={data.farmer.full_name}
                          className="h-16 w-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-lg font-medium text-gray-700">
                            {getInitials(data.farmer?.full_name)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold dark:text-white">
                        {data.farmer?.full_name || `${data.farmer?.firstname} ${data.farmer?.lastname}`}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">{data.farmer?.email}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <BsTelephone className="text-gray-400" />
                      <span className="dark:text-white">{data.farmer?.phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <BsGeoAlt className="text-gray-400" />
                      <span className="dark:text-white">
                        {data.farmer?.community}, {data.farmer?.district}, {data.farmer?.region}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <BsPerson className="text-gray-400" />
                      <span className="dark:text-white capitalize">{data.farmer?.gender}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Section */}
            <div className="bg-white p-2 rounded-lg shadow-sm dark:bg-dark">
              <div className="border p-4 rounded-lg border-gray-200 dark:border-border-dark">
                <h3 className="text-lg font-semibold mb-4 dark:text-white">
                  Appointment purpose
                </h3>

                <p className="text-gray-700 dark:text-gray-300">
                  {data.message || 'No message provided'}
                </p>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !data && (
          <div className="w-full h-96 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400">No appointment data available</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default AppointmentDetailView
